import { useMemo, useState, useEffect, useContext, useRef } from 'react'
import { useTheme } from '@mui/material/styles'

import moment from 'moment-timezone'
import _ from 'lodash'
import { save } from 'save-file'
import { enqueueSnackbar } from 'notistack'

import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import { Box, MenuItem } from '@mui/material'

import VisibilityIcon from '@mui/icons-material/Visibility'
import SaveAltIcon from '@mui/icons-material/SaveAlt'

import { AppContext } from '/providers/app-context'
import { getInvoices } from '../../providers/clients-api.js'
import makeInvoicePDF from './invoice-pdf.js'
import StyledPaper from '../paper.jsx'
import { createTableStyles } from '../table-styles'

const InvoicesTable = () => {
  const theme = useTheme()
  const tableStyles = useMemo(() => createTableStyles(theme), [theme])

  const { apiSource, apiKey } = useContext(AppContext)
  const rowVirtualizerInstanceRef = useRef(null)

  const [sorting, setSorting] = useState([])
  const [loading, setLoading] = useState(false)
  const [invoices, setInvoices] = useState([])
  const invoiceVariables = useRef({})

  const getInvoiceList = async () => {
    setLoading(true)
    const result = await getInvoices({ apiSource, apiKey })
    if (result.error) {
      enqueueSnackbar({ message: result.error, variant: 'error' })
    } else {
      setInvoices(result.invoices)
      invoiceVariables.current = result.variables
    }
    setLoading(false)
  }

  useEffect(() => {
    getInvoiceList()
  }, [])

  useEffect(() => {
    //scroll to the top of the table when the sorting changes
    try {
      if (invoices.length) {
        rowVirtualizerInstanceRef.current?.scrollToIndex?.(0)
      }
    } catch (error) {
      console.error(error)
    }
  }, [sorting])

  const viewInvoice = async invoice => {
    const pdfBlob = await makeInvoicePDF(invoice, invoiceVariables.current)
    const url = URL.createObjectURL(pdfBlob)
    window.open(url)
  }

  const downloadInvoice = async invoice => {
    const pdfBlob = await makeInvoicePDF(invoice, invoiceVariables.current)
    await save(pdfBlob, invoice.invoicenumber + '.pdf')
  }

  const columns = useMemo(() => {

    let cols = [
      {
        accessorKey: 'invoicenumber',
        header: 'Invoice Number',
        filterVariant: 'autocomplete',
        size: 200,
        Cell: ({ cell, row }) => <div className='link' onClick={() => { viewInvoice(row.original) }}><VisibilityIcon className='mr-5 grey' />{cell.getValue()}</div>
      },
      {
        id: 'invoice_date',
        accessorFn: (originalRow) => moment(originalRow.invoice_date, "YYYYMMDDHHmmss"),
        header: 'Date',
        filterVariant: 'date-range',
        sortingFn: 'datetime',
        size: 200,
        Cell: ({ cell }) => {
          const date = cell.getValue()
          if (date.isValid()) return date.format("DD MMM YYYY")
          return "-"
        }
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        size: 200,
        Cell: ({ cell }) => moneyFormat(cell.getValue()),
        muiTableBodyCellProps: {
          align: 'right',
        },
        muiTableHeadCellProps: {
          align: 'right',
        },
      },
      {
        accessorKey: 'vat',
        header: 'VAT',
        size: 200,
        Cell: ({ cell }) => moneyFormat(cell.getValue()),
        muiTableBodyCellProps: {
          align: 'right',
        },
        muiTableHeadCellProps: {
          align: 'right',
        },
      },
      {
        accessorKey: 'total',
        header: 'Total',
        size: 200,
        Cell: ({ cell }) => moneyFormat(cell.getValue()),
        muiTableHeadCellProps: {
          align: 'right',
        },
        muiTableBodyCellProps: {
          align: 'right',
        },
      },
    ]
    return cols
  }, [])

  const getRowActions = ({ closeMenu, row }) => {
    let actions = [
      <MenuItem key={1} onClick={() => { viewInvoice(row.original); closeMenu() }}><VisibilityIcon className='mr-2' />View Invoice</MenuItem>,
      <MenuItem key={2} onClick={() => { downloadInvoice(row.original); closeMenu() }}><SaveAltIcon className='mr-2' /> Download Invoice</MenuItem>,
    ]

    return actions
  }


  const table = useMaterialReactTable({
    columns,
    data: invoices,
    state: {
      isLoading: loading,
      sorting,
      showProgressBars: loading,
    },
    initialState: { showColumnFilters: false },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: true,
    enableBatchRowSelection: true,
    enableRowActions: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableRowVirtualization: true,
    rowVirtualizerInstanceRef,
    positionToolbarAlertBanner: "none",
    onSortingChange: setSorting,
    getRowId: (originalRow) => originalRow.invoice_id,
    renderRowActionMenuItems: ({ closeMenu, row }) => getRowActions({ closeMenu, row }),
    ...tableStyles,

    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),
  })

  return (
    <Box sx={{ height: 'calc(100vh - 185px)', width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <StyledPaper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} height="100vh">
        <MaterialReactTable table={table} />
      </StyledPaper>
    </Box>
  )
}

export default InvoicesTable

const moneyFormat = value => <span className={value === 0 ? "grey" : ""}>{value ? "£" + value.toFixed(2) : "-"}</span>